import type { ArtSource, Block, CompileResult, PageSummary } from '@textbox/core';

export type Role = 'editor' | 'admin';
export interface User { id: number; email: string; name: string; role: Role }

export interface Article {
  id: string; headline: string; standfirst: string; body: Block[]; category: string; region: string | null; priority: number;
  publishAt: string | null; expireAt: string | null; relatedPages: number[]; status: 'draft' | 'published'; author: string | null; createdAt: string; updatedAt: string;
}
export type ArticleInput = Omit<Article, 'id' | 'status' | 'createdAt' | 'updatedAt' | 'author'>;

export type Colour = 'black' | 'red' | 'green' | 'yellow' | 'blue' | 'magenta' | 'cyan' | 'white';
export type FastextSpec = { red: [string, number]; green: [string, number]; yellow: [string, number]; blue: [string, number] };
export interface Section {
  key: string; title: string; indexPage: number; firstPage: number; lastPage: number; categories: string[]; band: Colour; titleColour: Colour;
  bannerAsset: string | null; bannerStyle: 'band' | 'bar'; fastext: FastextSpec; showOnIndex: boolean; indexLabel: string | null; sort: number; enabled: boolean;
}
export interface Asset { id: string; name: string; kind: 'art' | 'tti'; width: number; height: number; rows: string[]; source: ArtSource | null; createdAt: string; updatedAt: string }
export type IndexEntry = { kind: 'section'; key: string } | { kind: 'page'; label: string; page: number };
export interface IndexSettings { logoAsset: string | null; entries: IndexEntry[]; footnotes: string[]; fastext: FastextSpec; editorialName: string }

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function call<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  });
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) throw new ApiError(res.status, (data as { error?: string } | null)?.error ?? `${res.status} ${res.statusText}`);
  return data as T;
}

export const api = {
  me: () => call<User>('GET', '/cms/me'),
  login: (email: string, password: string) => call<User>('POST', '/cms/login', { email, password }),
  logout: () => call<{ ok: true }>('POST', '/cms/logout'),

  users: () => call<User[]>('GET', '/cms/users'),
  createUser: (u: { email: string; name: string; role: Role; password: string }) => call<User>('POST', '/cms/users', u),
  deleteUser: (id: number) => call<{ ok: true }>('DELETE', `/cms/users/${id}`),

  articles: () => call<Article[]>('GET', '/cms/articles'),
  article: (id: string) => call<Article>('GET', `/cms/articles/${id}`),
  createArticle: (a: ArticleInput) => call<Article>('POST', '/cms/articles', a),
  updateArticle: (id: string, a: ArticleInput) => call<Article>('PUT', `/cms/articles/${id}`, a),
  deleteArticle: (id: string) => call<{ ok: true }>('DELETE', `/cms/articles/${id}`),
  publish: (id: string) => call<Article>('POST', `/cms/articles/${id}/publish`),
  unpublish: (id: string) => call<Article>('POST', `/cms/articles/${id}/unpublish`),

  sections: () => call<Section[]>('GET', '/cms/sections'),
  saveSection: (s: Section) => call<Section>('PUT', `/cms/sections/${s.key}`, s),
  deleteSection: (key: string) => call<{ ok: true }>('DELETE', `/cms/sections/${key}`),

  index: () => call<IndexSettings>('GET', '/cms/index'),
  saveIndex: (s: IndexSettings) => call<IndexSettings>('PUT', '/cms/index', s),

  assets: () => call<Asset[]>('GET', '/cms/assets'),
  asset: (id: string) => call<Asset>('GET', `/cms/assets/${id}`),
  createAsset: (a: Pick<Asset, 'name' | 'kind' | 'width' | 'height' | 'rows' | 'source'>) => call<Asset>('POST', '/cms/assets', a),
  updateAsset: (id: string, a: Pick<Asset, 'name' | 'kind' | 'width' | 'height' | 'rows' | 'source'>) => call<Asset>('PUT', `/cms/assets/${id}`, a),
  deleteAsset: (id: string) => call<{ ok: true }>('DELETE', `/cms/assets/${id}`),
  importTti: (name: string, tti: string, rows?: [number, number]) => call<Asset>('POST', '/cms/assets/import', { name, tti, rows }),

  preview: (body: { kind: 'article'; article: Partial<ArticleInput>; sectionKey?: string } | { kind: 'section'; section: Section } | { kind: 'index'; settings: IndexSettings }) =>
    call<CompileResult>('POST', '/cms/preview', body),
  pages: () => call<PageSummary[]>('GET', '/cms/pages'),
};
